import { useEffect, useMemo, useState } from "react";

import { Header } from "@components/Header";
import { PaperCard } from "@components/PaperCard";
import { FilterPanel } from "@components/FilterPanel";
import { TopicPills } from "@components/TopicPills";
import { RANGE_OPTIONS, type DateRange, type RangeSelection, useDataset } from "@hooks/useDataset";
import { useFilters } from "@hooks/useFilters";
import { countByTopic } from "@lib/sort";
import { Toaster } from "sonner";

function renderEmptyState(message: string) {
  return (
    <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center text-sm text-slate-500">
      {message}
    </div>
  );
}

export default function App() {
  const [rangeSelection, setRangeSelection] = useState<RangeSelection>(7);
  const [customRange, setCustomRange] = useState<DateRange>({ startDate: "", endDate: "" });
  const effectiveCustomRange = rangeSelection === "custom" ? customRange : null;
  const { data, loading, error, availableDates } = useDataset(rangeSelection, effectiveCustomRange);
  const papers = data?.papers ?? [];
  const { filters, setSearch, setTopic, papers: filteredPapers } = useFilters(papers);

  useEffect(() => {
    if (rangeSelection !== "custom") {
      return;
    }
    if (customRange.startDate && customRange.endDate) {
      return;
    }
    const latest = availableDates.reduce(
      (current, entry) => (entry.date > current ? entry.date : current),
      availableDates[0]?.date ?? "",
    );
    if (latest) {
      setCustomRange({ startDate: latest, endDate: latest });
    }
  }, [rangeSelection, availableDates, customRange.startDate, customRange.endDate]);

  function handleCustomStart(value: string): void {
    setCustomRange((prev) => ({ ...prev, startDate: value }));
  }

  function handleCustomEnd(value: string): void {
    setCustomRange((prev) => ({ ...prev, endDate: value }));
  }

  const topicCounter = useMemo(() => countByTopic(papers), [papers]);
  const latestCollectedAt = data?.runs?.[0]?.collected_at ?? null;

  const topics = useMemo(() => {
    if (!data?.topics) {
      return [];
    }
    return data.topics.map((topic) => ({
      name: topic.name,
      count: topicCounter.get(topic.name) ?? topic.paper_count,
      lastFetchedAt: topic.last_fetched_at,
    }));
  }, [data?.topics, topicCounter]);

  return (
    <div className="min-h-screen bg-slate-100 pb-20">
      <Toaster position="top-center" />
      <Header
        latestCollectedAt={latestCollectedAt}
        generatedAt={data?.generated_at ?? null}
        totalPapers={papers.length}
        runCount={data?.runs?.length ?? 0}
      />
      
      <main className="mx-auto max-w-3xl px-4 sm:px-6">
        <TopicPills topics={topics} active={filters.topic} onSelect={setTopic} />
        
        <div className="mt-6">
          <FilterPanel
            rangeOptions={RANGE_OPTIONS}
            activeRange={rangeSelection}
            onRangeChange={setRangeSelection}
            customStartDate={customRange.startDate}
            customEndDate={customRange.endDate}
            onCustomStartChange={handleCustomStart}
            onCustomEndChange={handleCustomEnd}
            searchValue={filters.search}
            onSearchChange={setSearch}
            filteredCount={filteredPapers.length}
            totalCount={papers.length}
          />
          
          <div className="mt-6 space-y-6">
            {loading && renderEmptyState("正在加载最新数据...")}
            {error && renderEmptyState(`加载失败：${error}`)}
            {!loading && !error && filteredPapers.length === 0 && renderEmptyState("没有找到符合条件的论文。")}
            {!loading && !error && filteredPapers.length > 0 && (
              <div className="space-y-6">
                {filteredPapers.map((paper) => (
                  <PaperCard
                    key={`${paper.paper_id}:${paper.topic}`}
                    paper={paper}
                    highlight={paper.collected_at === latestCollectedAt}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
